import React, { useState } from 'react';

import { ClipboardDocumentCheckIcon, ClipboardDocumentIcon } from '@heroicons/react/24/solid';
import { useClipboard } from '@hooks/useClipboard';
import { IconButton } from '@material-tailwind/react';
import { animated, useSpring } from '@react-spring/web';

type PropsType = {
  label: string | React.ReactNode;
  text: string;
};

const HiddenString: React.FC<PropsType> = ({ text, label }) => {
  const [isMouseLeave, setIsMouseLeave] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const maskedText = text.slice(0, 4) + '*'.repeat(text.length - 8) + text.slice(-4);

  const copy = useClipboard(text);

  const [copyAnimationStyles, copyAnimationApi] = useSpring(() => ({
    from: { opacity: 1 },
    reset: true,
    config: {
      duration: 200,
    },
  }));
  const [copiedAnimationStyles, copiedAnimationApi] = useSpring(() => ({
    from: { opacity: 1 },
    reset: true,
    config: {
      duration: 200,
    },
  }));

  const handleCopy = async (text: string) => {
    await copy(text);
    copyAnimationApi.start({
      from: {
        opacity: 1,
      },
      to: {
        opacity: 0,
      },
      onRest() {
        setIsCopied(true);
        copiedAnimationApi.start({
          from: {
            opacity: 0,
          },
          to: {
            opacity: 1,
          },
        });
      },
    });
  };

  const handleMouseLeave = () => {
    setIsMouseLeave(true);
  };

  const handleMouseOver = () => {
    if (isMouseLeave) {
      copiedAnimationApi.start({
        from: {
          opacity: 1,
        },
        to: {
          opacity: 0,
        },
        onRest() {
          setIsCopied(false);
          setIsMouseLeave(false);
          copyAnimationApi.start({
            from: {
              opacity: 0,
            },
            to: {
              opacity: 1,
            },
          });
        },
      });
    }
  };

  const copyButton = (
    <IconButton
      variant="text"
      size="sm"
      className="h-[25px]"
      onClick={() => handleCopy(text)}
      onMouseLeave={handleMouseLeave}
    >
      <ClipboardDocumentIcon className="text-white h-[20px]" />
    </IconButton>
  );

  return (
    <div>
      <p className="text-sm text-blue-300 font-semibold">{label}</p>
      <div className="flex">
        <p className="text-base text-white font-semibold break-all">{maskedText}</p>
        {!isCopied && <animated.div style={copyAnimationStyles}>{copyButton}</animated.div>}
        {isCopied && (
          <animated.div style={copiedAnimationStyles}>
            <IconButton
              variant="text"
              size="sm"
              className="h-[25px]"
              onMouseOver={handleMouseOver}
              onMouseLeave={handleMouseLeave}
            >
              <ClipboardDocumentCheckIcon className="text-white h-[20px]" />
            </IconButton>
          </animated.div>
        )}
      </div>
    </div>
  );
};

export default React.memo(HiddenString);
